import {
  type DirectPaymentOrderCreateSchema,
  type DirectPaymentOrderDetail,
  type DirectPaymentOrderUpdateRequest,
  type DirectPaymentOrdersListQuerySchema,
  type DirectPaymentQuote,
  type DirectPaymentQuoteQuery,
  type Paginated,
  type PaymentDetail,
  type PaymentsListQuerySchema,
  type PresignedUpload,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The contract's own INPUT shape (U3) — never a hand-written copy of the filters. */
type OrdersParams = z.input<typeof DirectPaymentOrdersListQuerySchema>;

/**
 * What a create actually sends, which is the schema's **input** and not its
 * output: `note` carries a `.default(null)`, so an omitted key is a valid body.
 */
type OrderCreateBody = z.input<typeof DirectPaymentOrderCreateSchema>;

/** The ledger's filters, as the contract spells them — `from`/`to` are instants here. */
type PaymentsParams = z.input<typeof PaymentsListQuerySchema>;

const ORDERS = '/admin/direct-payment-orders';
const PAYMENTS = '/admin/billing/payments';

/**
 * The staff-managed payment channel, as an operator reaches it — the `Payments`
 * module's api.
 *
 * ## It reads plans and never writes one
 *
 * There is no product endpoint here. The order desk offers plans through the
 * shared product **lookup** and stays in step with the catalogue over
 * `billing.product.changed` (W5/W6) — the division `user-lookup` already draws
 * for members, and the reason `subscriptionsApi` is a separate cache rather than
 * a billing super-api.
 *
 * ## Every write re-reads, and none of them writes the response into the cache
 *
 * The five transitions all answer with the order. Rendering from that answer
 * would give the screen two sources for one row, and they disagree the moment a
 * colleague decides a second later — which on this surface is not a cosmetic
 * problem: the row says whether somebody who paid has access.
 *
 * ## The refusal is the point, not the exception
 *
 * `billing_order_not_claimable` is a **409 for all five** transitions and it is
 * what a double confirm produces. Nothing here retries and nothing here
 * swallows it: the caller unwraps, reads `details.status[0]`, and says which way
 * the order actually went.
 *
 * ## The ledger reads live here, not beside the store trail (P31b)
 *
 * `billingEventsApi` is its own cache because nothing this slice writes touches
 * a delivery. The ledger is the opposite case: four of the transitions above —
 * submit, confirm, reject, refund — each **write a ledger row** through the
 * api's single writer, so the list that shows those rows has to be invalidated
 * by the mutations that produce them, and RTK can only do that inside one api
 * (W5's same-slice rule). The store channel's rows arrive through a webhook the
 * browser cannot observe, so the page polls for those, the way the trail does.
 */
export const paymentsApi = createApi({
  reducerPath: 'paymentsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['DirectPaymentOrders', 'DirectPaymentOrder', 'Payments', 'Payment'],
  endpoints: (builder) => ({
    listOrders: builder.query<Paginated<DirectPaymentOrderDetail>, OrdersParams>({
      query: (params) => ({ url: ORDERS, method: 'GET', params }),
      providesTags: ['DirectPaymentOrders'],
    }),

    order: builder.query<DirectPaymentOrderDetail, string>({
      query: (id) => ({ url: `${ORDERS}/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'DirectPaymentOrder' as const, id }],
    }),

    /** Every money movement, from every channel — the ledger (P31). Read-only by design. */
    listPayments: builder.query<Paginated<PaymentDetail>, PaymentsParams>({
      query: (params) => ({ url: PAYMENTS, method: 'GET', params }),
      providesTags: ['Payments'],
    }),

    /**
     * One ledger row by id — for a deep link to a row the loaded page does not
     * hold. A row already on screen is drawn from the list, never re-read.
     */
    payment: builder.query<PaymentDetail, string>({
      query: (id) => ({ url: `${PAYMENTS}/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Payment' as const, id }],
    }),

    /**
     * What this member would pay for this plan — a read that **claims nothing**.
     *
     * It runs the same price-and-discount path the draft and the submit run, so
     * the entry form can never show a number the order will not honour. It is
     * still not a promise: a grant can expire, be revoked with a ban, or be
     * spent by another order between this answer and the submit that reserves
     * it, and the submit re-resolves.
     *
     * Untagged on purpose. A quote is a question about *right now*; caching it
     * against a tag would mean deciding when a price stops being true, and
     * nothing on this client knows that.
     */
    quote: builder.query<DirectPaymentQuote, DirectPaymentQuoteQuery>({
      query: (params) => ({ url: `${ORDERS}/quote`, method: 'GET', params }),
    }),

    createOrder: builder.mutation<DirectPaymentOrderDetail, OrderCreateBody>({
      query: (body) => ({ url: ORDERS, method: 'POST', body }),
      invalidatesTags: ['DirectPaymentOrders'],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceOrderChange(queryFulfilled.then(({ data }) => data));
      },
    }),

    updateOrder: builder.mutation<
      DirectPaymentOrderDetail,
      { id: string; body: DirectPaymentOrderUpdateRequest }
    >({
      query: ({ id, body }) => ({ url: `${ORDERS}/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'DirectPaymentOrder' as const, id },
        'DirectPaymentOrders' as const,
      ],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceOrderChange(queryFulfilled.then(({ data }) => data));
      },
    }),

    /**
     * Fixes the price and **reserves** the member's earned discounts (N27).
     *
     * Bodyless: the route takes no `@Body`, and the terms are deliberately
     * re-resolved server-side at this moment rather than read off the draft — a
     * draft entered last week quotes last week's price.
     */
    submitOrder: builder.mutation<DirectPaymentOrderDetail, { id: string }>({
      query: ({ id }) => ({ url: `${ORDERS}/${id}/submit`, method: 'POST' }),
      invalidatesTags: decidedTags,
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceOrderChange(queryFulfilled.then(({ data }) => data));
      },
    }),

    /** Grants the entitlement. Irreversible except by a refund. Bodyless. */
    confirmOrder: builder.mutation<DirectPaymentOrderDetail, { id: string }>({
      query: ({ id }) => ({ url: `${ORDERS}/${id}/confirm`, method: 'POST' }),
      invalidatesTags: decidedTags,
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceOrderChange(queryFulfilled.then(({ data }) => data));
      },
    }),

    /** Refuses the order and gives the member's claimed grants back. */
    rejectOrder: builder.mutation<DirectPaymentOrderDetail, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `${ORDERS}/${id}/reject`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: decidedTags,
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceOrderChange(queryFulfilled.then(({ data }) => data));
      },
    }),

    /**
     * Records that a confirmed order was refunded, which **revokes** the
     * entitlement it granted — and deliberately does not give the discount
     * grants back (`REFUND_KEEPS_GRANTS_SPENT`).
     */
    refundOrder: builder.mutation<DirectPaymentOrderDetail, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `${ORDERS}/${id}/refund`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: decidedTags,
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceOrderChange(queryFulfilled.then(({ data }) => data));
      },
    }),

    /**
     * Throws a **draft** away. Refused past `draft`, because a submitted order
     * has the member's grants reserved and a confirmed one is a financial
     * record.
     */
    discardOrder: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({ url: `${ORDERS}/${id}`, method: 'DELETE' }),
      invalidatesTags: decidedTags,
      async onQueryStarted({ id }, { queryFulfilled }) {
        // `204` — there is no row to read the member off, and the order is still
        // what the queue has to drop.
        await announceOrderChange(queryFulfilled.then(() => ({ id, userId: null })));
      },
    }),

    /**
     * Step 1 of attaching the receipt (P23b4): the constrained presigned PUT.
     *
     * **Not `mediaApi`'s presign.** That one is `POST /admin/media/presign-upload`,
     * authorized by `brand.manage` and scoped to the platform's brand library. A
     * receipt is presigned through the ORDER it belongs to, so the route can
     * refuse an id that does not exist before minting a slot for it — and so the
     * kind is the server's to name rather than the client's.
     *
     * Invalidates nothing: presigning changes no row. The attach below does.
     */
    presignOrderEvidence: builder.mutation<
      PresignedUpload,
      { id: string; mime: string; sizeBytes: number }
    >({
      query: ({ id, mime, sizeBytes }) => ({
        url: `${ORDERS}/${id}/evidence/presign-upload`,
        method: 'POST',
        body: { mime, sizeBytes },
      }),
    }),

    /** Step 3: finalize the upload and bind it to the order, in one request. */
    attachOrderEvidence: builder.mutation<
      DirectPaymentOrderDetail,
      { id: string; mediaId: string }
    >({
      query: ({ id, mediaId }) => ({
        url: `${ORDERS}/${id}/evidence`,
        method: 'POST',
        body: { mediaId },
      }),
      invalidatesTags: decidedTags,
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceOrderChange(queryFulfilled.then(({ data }) => data));
      },
    }),

    /**
     * Removes the receipt and schedules its bytes for purging.
     *
     * The one destructive act on this surface, and it answers with the order
     * rather than 204 — the section that called it has to redraw what it emptied.
     */
    detachOrderEvidence: builder.mutation<DirectPaymentOrderDetail, { id: string }>({
      query: ({ id }) => ({ url: `${ORDERS}/${id}/evidence`, method: 'DELETE' }),
      invalidatesTags: decidedTags,
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceOrderChange(queryFulfilled.then(({ data }) => data));
      },
    }),
  }),
});

/**
 * A decision moves the order AND the ledger row that records it — a confirm
 * takes `pending` to `succeeded`, a refund `succeeded` to `refunded` — so the
 * list and any single row are stale afterwards. The order's id says nothing
 * about which payment id that is, so `Payment` is invalidated as a whole type.
 */
function decidedTags(
  _result: unknown,
  _error: unknown,
  { id }: { id: string },
): ({ type: 'DirectPaymentOrder'; id: string } | 'DirectPaymentOrders' | 'Payments' | 'Payment')[] {
  return [
    { type: 'DirectPaymentOrder' as const, id },
    'DirectPaymentOrders' as const,
    'Payments' as const,
    'Payment' as const,
  ];
}

async function announceOrderChange(
  landed: Promise<{ id: string; userId: string | null }>,
): Promise<void> {
  try {
    const order = await landed;

    EVENT_BUS.publish({
      type: 'billing.order.changed',
      orderId: order.id,
      userId: order.userId,
    });
  } catch {
    // Refused — nothing to announce. Swallowed deliberately: `onQueryStarted`
    // has no caller, so a rejection escaping it becomes an unhandled rejection
    // that exits the runner non-zero beside a wholly green suite (P22b1). The
    // failure is already the caller's, through `unwrap()`.
  }
}

export const {
  useListOrdersQuery,
  useOrderQuery,
  useListPaymentsQuery,
  usePaymentQuery,
  useQuoteQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
  useSubmitOrderMutation,
  useConfirmOrderMutation,
  usePresignOrderEvidenceMutation,
  useAttachOrderEvidenceMutation,
  useDetachOrderEvidenceMutation,
  useRejectOrderMutation,
  useRefundOrderMutation,
  useDiscardOrderMutation,
} = paymentsApi;
